import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ClickAwayListener, Tooltip } from '@mui/material';

// Tap/hover expansion is enabled only when CSS has actually clipped the text.
export default function ExpandableText({ text, children, className, style }) {
    const [open, setOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const textRef = useRef(null);
    const plain = typeof text === 'string' ? text : '';
    const isClipped = useCallback(element => {
        if (!element) return false;
        if (element.scrollWidth > element.clientWidth + 1 || element.scrollHeight > element.clientHeight + 1) return true;
        const parent = element.parentElement;
        if (!parent) return false;
        const parentStyle = window.getComputedStyle(parent);
        if (!['hidden', 'clip'].includes(parentStyle.overflowX) && !['hidden', 'clip'].includes(parentStyle.overflow)) return false;
        const elementBounds = element.getBoundingClientRect();
        const parentBounds = parent.getBoundingClientRect();
        return elementBounds.right > parentBounds.right + 1 || elementBounds.bottom > parentBounds.bottom + 1;
    }, []);
    const measure = useCallback(() => {
        const element = textRef.current;
        if (!element) return;
        const clipped = isClipped(element);
        setCollapsed(clipped);
        if (!clipped) setOpen(false);
    }, [isClipped]);
    useLayoutEffect(measure, [measure, plain, children]);
    useEffect(() => {
        const element = textRef.current;
        const observer = typeof ResizeObserver !== 'undefined' && element ? new ResizeObserver(measure) : null;
        if (observer && element) observer.observe(element);
        window.addEventListener('resize', measure);
        document.fonts?.ready?.then(measure).catch(() => {});
        return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
    }, [measure]);
    const show = () => { measure(); if (isClipped(textRef.current)) setOpen(true); };
    return <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Tooltip title={collapsed ? plain : ''} open={collapsed && open} onClose={() => setOpen(false)} disableTouchListener
            componentsProps={{ tooltip: { sx: { maxWidth: 'min(90vw, 36rem)', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap', fontSize: '.9rem' } } }}>
            <span ref={textRef} className={className} style={{ cursor: collapsed ? 'pointer' : undefined, ...style }}
                role={collapsed ? 'button' : undefined} tabIndex={collapsed ? 0 : undefined}
                onClick={collapsed ? show : undefined} onFocus={collapsed ? show : undefined} onBlur={() => setOpen(false)}
                onMouseEnter={collapsed ? show : undefined} onMouseLeave={() => setOpen(false)}
                onKeyDown={collapsed ? event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); show(); } } : undefined}>
                {children ?? text}
            </span>
        </Tooltip>
    </ClickAwayListener>;
}
